import { useCallback } from "react";

import { useKeyedCompute } from "@/store/useKeyedCompute";
import {
  type AcknowledgementRequirement,
  acknowledgementCollections,
  acknowledgementFeedsAffected,
  feedUnacknowledgedCount,
} from "./acknowledgements";
import type { TaskFold } from "./tasks";

/**
 * How many things wait on an acknowledgement in each of the given feeds, recomputed as the log, items and events land.
 *
 * Zero means the channel is handled. What counts is `requires`, the view's Requires Acknowledgement list resolved. `now` moves the lookback
 * boundary; hand it a minute clock so old traffic ages out without a reload. A write recounts only the channels it
 * touched, and the map keeps its identity while no count changes. An item an open task in `tasks` names is not
 * counted while the task stays open.
 */
export function useFeedsUnacknowledgedCounts(
  feedIds: readonly string[],
  now: number,
  requires: AcknowledgementRequirement,
  tasks: TaskFold,
): ReadonlyMap<string, number> {
  const countFeed = useCallback(
    (feedId: string) => feedUnacknowledgedCount(feedId, now, requires, tasks),
    [now, requires, tasks],
  );
  return useKeyedCompute(
    "Dispatch:acknowledgements",
    acknowledgementCollections,
    feedIds,
    countFeed,
    acknowledgementFeedsAffected,
    [now, requires, tasks],
  );
}
